import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  SearchOutlined,
  PlusOutlined,
  UserSwitchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  FilterOutlined,
  ReloadOutlined,
  TeamOutlined,
  GroupOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { notification, Button, Select, Dropdown, Menu, Modal, Tooltip, Badge, Input, Radio, Collapse, Card, Divider, Avatar, Typography, Spin } from 'antd';
import PropertyCard from "./PropertyCard";
import PropertyTable from "./PropertyTable";
import NewPropertyForm from "./properties/NewPropertyForm";
import EditPropertyForm from "./properties/EditPropertyForm";
import PropertyRecurrencePanel from "./properties/PropertyRecurrencePanel";
import DateTimeModal from "./DateTimeModal";
import ImpersonationBanner from "./admin/Users/ImpersonationBanner";
import UserSelector from "./admin/properties/UserSelector";
import adminService from "../services/adminService";
import propertiesService from "../services/propertiesService";
import cartService from "../services/cartService";
import userService from "../services/userService";
import userImpersonationService from "../services/userImpersonationService";
import userRoleUtils from "../utils/userRoleUtils";
import eventBus from "../utils/eventBus";
import { useCart } from '../context/CartContext';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;
const { Panel } = Collapse;
const { Text } = Typography;

const PAGE_SIZE = 40;

const AdminPropertiesPage = ({ data, refreshProperties, setCartItems, removeFromCart, shedule }) => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Fixed: Initialize selectedUser from localStorage, but wait for users to load
  const [selectedUser, setSelectedUser] = useState(() => {
    const savedUser = localStorage.getItem('admin_selected_user') ?? null;
    if (savedUser === "null" || savedUser === "undefined") {
      return null;
    }
    try {
      return JSON.parse(savedUser) ?? null;
    } catch (error) {
      localStorage.setItem('admin_selected_user', null);
      return null;
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewPropertyForm, setShowNewPropertyForm] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [recurringProperty, setRecurringProperty] = useState(null);
  const [dateTimeModalVisible, setDateTimeModalVisible] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [groupByClient, setGroupByClient] = useState(false);
  const [sortField, setSortField] = useState("property_title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [bulkActionMenuVisible, setBulkActionMenuVisible] = useState(false);
  const [cart, setCart] = useState([]);
  const { addToCart, refreshCart } = useCart();
  const [groupedProperties, setGroupedProperties] = useState({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusUpdatingIds, setStatusUpdatingIds] = useState([]);
  const scrollContainerRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const loadMoreFnRef = useRef(() => {});
  const pendingAutoLoadRef = useRef(false);
  const lastAutoAppendAtRef = useRef(0);
  const canManagePropertyStatus = userRoleUtils.canManagePropertyStatus();
  const canManageRecurrence = userRoleUtils.canManagePropertyRecurrence();

  // Fixed: Save selectedUser to localStorage whenever it changes
  useEffect(() => {
    if (selectedUser !== null) {
      localStorage.setItem('admin_selected_user', JSON.stringify(selectedUser));
    } else {
      localStorage.setItem('admin_selected_user', null );
    }
  }, [selectedUser]);

  useEffect(() => {
    if( localStorage.getItem('admin_selected_user') === "undefined") {
      // If no user is selected, fetch users
      localStorage.setItem('admin_selected_user', null);
    }
    // Subscribe to cart updates
    const unsubscribe = eventBus.on('cart:updated', (updatedCart) => {
      if (updatedCart) {
        setCart(updatedCart);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Make sure cart state is properly initialized
  useEffect(() => {
    if (data?.cart) {
      setCart(data.cart);
    } else {
      // If no cart data, fetch it
      refreshCart();
    }
  }, [data?.cart]);

  // Fetch properties and users on component mount
  useEffect(() => {
    fetchUsers();
    fetchProperties({ pageToLoad: 1, replace: true });
    refreshCart();
  }, []);

  // Re-fetch from server when filters/search/sort change (debounced so typing doesn't spam)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchProperties({ pageToLoad: 1, replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, selectedUser, sortField, sortOrder]);

  // Create grouped properties whenever filtered properties change
  useEffect(() => {
    createGroupedProperties();
  }, [filteredProperties, users]);

  const removePropertyFromCart = async (propertyId) => {
    try {
      setLoading(true);
      
      const response = await cartService.removeFromCart(0, propertyId);
      
      if (response && response.status) {
        setCart(prevCart => {
          const newCart = { ...prevCart };
          delete newCart[propertyId];
          return newCart;
        });
        
        eventBus.emit('cart:updated', cart);
        await refreshCart();
        
        notification.success({
          message: "Removed from Cart",
          description: "Property removed from cart successfully!"
        });
        
        return true;
      } else {
        throw new Error(response?.message || 'Failed to remove from cart');
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      notification.error({
        message: "Failed to remove from cart!",
        description: error.message || "An error occurred while removing from cart.",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async ({ pageToLoad = 1, replace = true } = {}) => {
    try {
      if (replace) setLoading(true);
      else setLoadingMore(true);

      if (replace && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }

      const params = {
        limit: PAGE_SIZE,
        page: pageToLoad,
        sort_field: sortField,
        sort_order: sortOrder
      };
      if (selectedUser) params.user_id = selectedUser;
      if (searchTerm) params.search = searchTerm;

      const response = await adminService.getProperties(params);
      const nextItems = Array.isArray(response?.properties) ? response.properties : [];
      const pagination = response?.pagination || {};

      setTotal(Number(pagination.total || 0));
      setHasMore(Number(pagination.current_page || pageToLoad) < Number(pagination.last_page || pageToLoad));
      setPage(Number(pagination.current_page || pageToLoad));

      if (replace) {
        setProperties(nextItems);
        setFilteredProperties(nextItems);
        eventBus.emit('properties:updated', nextItems);
      } else {
        setProperties((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev];
          for (const p of nextItems) {
            if (!seen.has(p.id)) merged.push(p);
          }
          return merged;
        });
        setFilteredProperties((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev];
          for (const p of nextItems) {
            if (!seen.has(p.id)) merged.push(p);
          }
          eventBus.emit('properties:updated', merged);
          return merged;
        });
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch properties'
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { limit: 300, roles: ['customer', 'agency'] };
      const response = await userService.getAllCustomers(params);
       
      if (response && response.data) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...properties];

    // Apply user filter if selected
    if (selectedUser) {
      result = result.filter(property => property.user_id === selectedUser);
    }

    // Apply search text filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(property =>
        property.property_title?.toLowerCase().includes(searchLower) ||
        property.apartment_address?.toLowerCase().includes(searchLower) ||
        property.delivery_zip_code?.toLowerCase().includes(searchLower) ||
        property.apartment_number?.toLowerCase().includes(searchLower) ||
        property.user?.name?.toLowerCase().includes(searchLower) ||
        property.user?.email?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    result = sortProperties(result);
    setFilteredProperties(result);
  };

  const maybeAppendMore = useCallback(() => {
    if (!hasMore) return;
    if (loadingMore) return;
    if (loading) return;
    // Very small throttle to avoid repeated state updates during fast scroll
    const now = Date.now();
    if (now - lastAutoAppendAtRef.current < 250) return;
    lastAutoAppendAtRef.current = now;
    fetchProperties({ pageToLoad: page + 1, replace: false });
  }, [hasMore, loadingMore, loading, fetchProperties, page]);

  // Keep latest load-more callback for observers
  useEffect(() => {
    loadMoreFnRef.current = maybeAppendMore;
  }, [maybeAppendMore]);

  // Trigger infinite loading reliably (even if window scrolls, not the inner div)
  useEffect(() => {
    const target = bottomSentinelRef.current;
    if (!target) return;

    const root = scrollContainerRef.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          // If we hit the sentinel while still loading, queue one extra attempt
          // so the first "load more" starts right after the initial page completes.
          pendingAutoLoadRef.current = true;
          loadMoreFnRef.current();
        }
      },
      { root, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [filteredProperties.length, viewMode, groupByClient, showNewPropertyForm, editingPropertyId]);

  // If sentinel was visible during initial load, retry once loading finishes.
  useEffect(() => {
    if (loading || loadingMore) return;
    if (!hasMore) {
      pendingAutoLoadRef.current = false;
      return;
    }
    if (!pendingAutoLoadRef.current) return;
    pendingAutoLoadRef.current = false;
    maybeAppendMore();
  }, [loading, loadingMore, hasMore, maybeAppendMore]);

  const handleScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!el) return;
      // If user is within 300px of bottom, append next chunk
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 300) {
        maybeAppendMore();
      }
    },
    [maybeAppendMore]
  );

  // New function to create grouped properties by client
  const createGroupedProperties = () => {
    const grouped = filteredProperties.reduce((groups, property) => {
      const userId = property.user_id;
      if (!groups[userId]) {
        groups[userId] = {
          user: getUserById(userId),
          properties: []
        };
      }
      groups[userId].properties.push(property);
      return groups;
    }, {});

    // Sort each group's properties
    Object.keys(grouped).forEach(userId => {
      grouped[userId].properties = sortProperties(grouped[userId].properties);
    });

    setGroupedProperties(grouped);
  };

  const sortProperties = (propertiesList) => {
    return [...propertiesList].sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];

      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();

      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
  };

  const handleClearFilters = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setSortField("property_title");
    setSortOrder("asc");
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
  };

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleEdit = (propertyId) => {
    setEditingPropertyId(propertyId);
  };

  const handleDelete = async (propertyId) => {
    try {
      const response = await propertiesService.deleteProperty(propertyId);
      if (response.status) {
        notification.success({
          message: "Successful",
          description: "Property deleted successfully!",
        });

        fetchProperties();
        refreshCart();
      } else {
        notification.error({
          message: "Failed to delete property!",
          description: response?.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while deleting the property.",
      });
    }
  };

  const handleToggleStatus = async (propertyId, newStatus) => {
    if (!canManagePropertyStatus) {
      notification.error({
        message: "Not allowed",
        description: "Only admin and superadmin users can activate or deactivate properties.",
      });
      return false;
    }

    const normalizedStatus = String(newStatus) === "1" ? "1" : "0";
    setStatusUpdatingIds((prev) => [...new Set([...prev, propertyId])]);

    try {
      const response = await propertiesService.updatePropertyStatus(propertyId, normalizedStatus);

      if (response?.status === false || response?.success === false) {
        throw new Error(response?.message || "Failed to update property status.");
      }

      if (response) {
        notification.success({
          message: "Status Updated", 
          description: normalizedStatus === "1" ? "Property activated successfully" : "Property deactivated successfully",
        });

        const updatedStatus = response.property?.status ?? normalizedStatus;
        const applyStatus = (items) =>
          items.map((prop) => prop.id === propertyId ? { ...prop, status: updatedStatus } : prop);

        setProperties(applyStatus);
        setFilteredProperties(applyStatus);

        if (normalizedStatus === "0") {
          if (response.cart) {
            setCart(response.cart);
            eventBus.emit('cart:updated', response.cart);
          } else if (cart && cart[propertyId]) {
            setCart((prevCart) => {
              const nextCart = { ...prevCart };
              delete nextCart[propertyId];
              eventBus.emit('cart:updated', nextCart);
              return nextCart;
            });
          }
          await refreshCart();
        }

        return true;
      }
    } catch (error) {
      console.error("Error updating property status:", error);
      notification.error({
        message: "Error",
        description: error.message || "Failed to update property status.",
      });
      return false;
    } finally {
      setStatusUpdatingIds((prev) => prev.filter((id) => id !== propertyId));
    }
  };

  const handleEditDateTime = (property) => {
    setSelectedProperty(property);
    setDateTimeModalVisible(true);
  };

  const handleImpersonateUser = async (userId) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      confirm({
        title: `Impersonate ${user.name || user.email}?`,
        content: 'You will be logged in as this user and can place orders on their behalf.',
        okText: 'Impersonate',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            setLoading(true);
            const response = await userImpersonationService.impersonateUser(userId);

            if (response.success) {
              notification.success({
                message: 'Impersonation Started',
                description: `You are now logged in as ${user.name || user.email}`
              });

              window.location.href = '/properties';
            } else {
              throw new Error(response.message || 'Failed to impersonate user');
            }
          } catch (error) {
            console.error('Error impersonating user:', error);
            notification.error({
              message: 'Impersonation Failed',
              description: error.message || 'Could not impersonate user'
            });
          } finally {
            setLoading(false);
          }
        }
      });
    } catch (error) {
      console.error('Error preparing impersonation:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to prepare user impersonation'
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingPropertyId(null);
  };

  const handleUpdateProperty = async () => {
    notification.success({
      message: "Successful",
      description: "Property updated successfully!",
    });
    setEditingPropertyId(null);
    fetchProperties();
  };

  // Fixed: Enhanced handleSaveProperty to refresh properties and emit events
  const handleSaveProperty = async (formData) => {
    try {
      let response = await propertiesService.createProperty(formData);
      if (response) {
        notification.success({
          message: "Success",
          description: "Property saved successfully!"
        });
        
        // Add to cart if property was created successfully
        if (response.property_id) {
          await addToCart(response.property_id);
        }
        
        // Refresh properties first to get the latest data
        await fetchProperties();
        
        // Then refresh cart
        await refreshCart();
        
        // Close the form
        setShowNewPropertyForm(false);
        
        // Emit events to update other components
        eventBus.emit('property:created', response);
        
      } else {
        console.error("Error saving property:", response);
      }
    } catch (error) {
      console.error("Error in handleSaveProperty:", error);
      notification.error({
        message: "Error",
        description: "Failed to save property"
      });
    }
  };

  const handleDateTimeModalClose = () => {
    setDateTimeModalVisible(false);
    setSelectedProperty(null);
  };

  const handleDateTimeSuccess = () => {
    fetchProperties();
    handleDateTimeModalClose();
  };

  const addToFromCart = async (property) => {
    try {
      const isActive = property?.status === 1 || property?.status === '1' || property?.status === true;
      if (!isActive) {
        notification.error({
          message: "Inactive property",
          description: "Inactive properties cannot be added to cart.",
        });
        return false;
      }

      setLoading(true);
      const dataToSend = new FormData();

      if (!userRoleUtils.isAdminOrImpersonating() && property.date < getMinimumDate()) {
        property.date = getMinimumDate();
      }

      const propertyData = {
        property_id: property.id.toString(),
        property_user_id: property.user_id,
        property_title: property.property_title,
        property_type: property.property_type_id.toString(),
        property_description: property.property_description,
        contact_name: property.contact_person,
        cleaner_information: property.cleaner_information,
        contact_number: property.contact_number,
        contact_description: property.contact_description || "",
        arrival_times: property.arrival_time
          ? property.arrival_time.split(":")
          : ["", ""],
        completion_times: property.completion_time
          ? property.completion_time.split(":")
          : ["", ""],
        date: property.date,
        quantity: parsePropertyJson(property.quantity),
        appartment_address: property.apartment_address,
        appartment_number: property.apartment_number,
        delivery_city: property.delivery_city,
        delivery_country: property.delivery_country,
        post_code: property.delivery_zip_code,
        product_quantity: parsePropertyJson(property.product_quantity)
      }

      dataToSend.append("type", 3);

      Object.entries(propertyData).forEach(([key, value]) => {
        if (
          ![
            "quantity",
            "product_quantity",
            "arrival_times",
            "completion_times",
          ].includes(key)
        ) {
          dataToSend.append(key, value);
        }
      });

      propertyData.arrival_times?.forEach((time) =>
        dataToSend.append("arrival_times[]", time)
      );
      propertyData.completion_times?.forEach((time) =>
        dataToSend.append("completion_times[]", time)
      );

      Object.entries(propertyData.quantity).forEach(([roomId, count]) => {
        dataToSend.append(`quantity[${roomId}]`, count);
      });

      Object.entries(propertyData.product_quantity).forEach(
        ([productId, product]) => {
          Object.entries(product).forEach(([field, value]) => {
            dataToSend.append(
              `product_quantity[${productId}][${field}]`,
              value
            );
          });
        }
      );

      if (userRoleUtils.isAdminOrImpersonating()) {
        dataToSend.append("userid", property.user_id || selectedUser);
      } else {
        dataToSend.append("userid", property.user_id);
      }

      const response = await propertiesService.updateProperty(dataToSend);
      if (response.status) {
        if (response.cart) {
          setCart(response.cart);
          eventBus.emit('cart:updated', response.cart);
        } else {
          await refreshCart();
        }

        notification.success({
          message: "Success",
          description: "Property added to cart successfully!"
        });

        return true;
      }
      else {
        notification.error({
          message: "Failed to add property to cart!",
          description: response?.message,
        });
        return false;
      }
    } catch (error) {
      console.error("Error adding property to cart:", error);
      notification.error({
        message: "Error",
        description: "An error occurred while adding property to cart.",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const forcedRefreshProperties = async () => {
    try {
      const response = await propertiesService.getAllProperties();

      if (response && response.properties) {
        setProperties(response.properties);

        if (response.order_schedule) {
          const existingSchedules = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
          const updatedSchedules = { ...existingSchedules };

          response.order_schedule.forEach(schedule => {
            if (schedule.property_id) {
              updatedSchedules[schedule.property_id] = {
                order_date: schedule.order_date,
                arrival_time: schedule.arrival_time,
                completion_time: schedule.completion_time
              };
            }
          });

          localStorage.setItem('dateIsSet', JSON.stringify(updatedSchedules));
          eventBus.emit('schedule:updated', updatedSchedules);
        }

        refreshCart();

        notification.success({
          message: 'Properties Refreshed',
          description: 'Properties list has been updated successfully.'
        });
      }
    } catch (error) {
      console.error('Error refreshing properties:', error);
      notification.error({
        message: 'Refresh Failed',
        description: 'Failed to refresh properties. Please try again.'
      });
    }
  };

  const getMinimumDate = () => {
    return userRoleUtils.getMinimumOrderDate();
  };

  const parsePropertyJson = (value) => {
    try {
      return JSON.parse(value || "{}");
    } catch (error) {
      return {};
    }
  };

  const handleBulkAddToCart = async () => {
    if (selectedPropertyIds.length === 0) {
      notification.warning({
        message: 'No properties selected',
        description: 'Please select at least one property'
      });
      return;
    }

    try {
      setLoading(true);

      let addedCount = 0;

      for (const propertyId of selectedPropertyIds) {
        const property = properties.find(p => p.id === propertyId);
        const isActive = property?.status === 1 || property?.status === '1' || property?.status === true;
        if (property && isActive) {
          const added = await addToFromCart(property);
          if (added) addedCount += 1;
        }
      }

      if (addedCount > 0) {
        notification.success({
          message: 'Success',
          description: `${addedCount} properties added to cart`
        });
      }

      setSelectedPropertyIds([]);
    } catch (error) {
      console.error('Error adding properties to cart:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to add some properties to cart'
      });
    } finally {
      setLoading(false);
      refreshCart();
    }
  };

  const handlePropertySelection = (propertyId, selected) => {
    if (selected) {
      setSelectedPropertyIds(prev => [...prev, propertyId]);
    } else {
      setSelectedPropertyIds(prev => prev.filter(id => id !== propertyId));
    }
  };

  const handleSelectAllProperties = (selected) => {
    if (selected) {
      setSelectedPropertyIds(filteredProperties.map(p => p.id));
    } else {
      setSelectedPropertyIds([]);
    }
  };

  const getUserById = (userId) => {
    return users.find(user => user.id === userId);
  };

  // Fixed: Enhanced getOwnerName function to handle the selected user display properly
  const getOwnerName = (user) => {
    if (!user) return "Unknown";
    
    // If user is just an ID (number or string), find the user object
    if (typeof user === 'number' || (typeof user === 'string' && !isNaN(user))) {
      const foundUser = getUserById(parseInt(user));
      if (foundUser) {
        return `${foundUser.first_name || ''} ${foundUser.last_name || ''}`.trim() || foundUser.name || foundUser.email || "Unknown";
      }
      return "Unknown";
    }
    
    // If user is already an object
    if (typeof user === 'object') {
      if (user.name) {
        return user.name;
      }
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || "Unknown";
    }
    
    return "Unknown";
  };

  // Fixed: Function to get selected user display name for filter badge
  const getSelectedUserDisplayName = () => {
    if (!selectedUser || users.length === 0) return "Unknown";
    
    const user = getUserById(selectedUser);
    if (user) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || user.email || "Unknown";
    }
    
    return "Unknown";
  };

  // Rest of the component remains the same...
  const bulkActionMenu = (
    <Menu>
      <Menu.Item key="addToCart" onClick={handleBulkAddToCart}>
        Add to Cart ({selectedPropertyIds.length})
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="clearSelection" onClick={() => setSelectedPropertyIds([])}>
        Clear Selection
      </Menu.Item>
    </Menu>
  );

  const sortMenu = (
    <Menu>
      <Menu.Item
        key="name"
        onClick={() => handleSortChange('property_title')}
        icon={sortField === 'property_title' ? (sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />) : null}
      >
        Property Name
      </Menu.Item>
      <Menu.Item
        key="created"
        onClick={() => handleSortChange('created_at')}
        icon={sortField === 'created_at' ? (sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />) : null}
      >
        Date Added
      </Menu.Item>
      <Menu.Item
        key="updated"
        onClick={() => handleSortChange('updated_at')}
        icon={sortField === 'updated_at' ? (sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />) : null}
      >
        Last Modified
      </Menu.Item>
    </Menu>
  );

  const viewMenu = (
    <Menu>
      <Menu.Item
        key="grid"
        onClick={() => { setViewMode("grid"); setGroupByClient(false); }}
        icon={<AppstoreOutlined />}
      >
        Grid View
      </Menu.Item>
      <Menu.Item
        key="list"
        onClick={() => { setViewMode("list"); setGroupByClient(false); }}
        icon={<AppstoreOutlined />}
      >
        List View
      </Menu.Item>
      <Menu.Item
        key="grouped"
        onClick={() => { setGroupByClient(true); }}
        icon={<GroupOutlined />}
      >
        Group by Client
      </Menu.Item>
    </Menu>
  );

  const renderGroupedView = () => {
    return (
      <div className="space-y-4" style={{ maxHeight: "100vh", overflowY: "auto" }}>
        {Object.entries(groupedProperties).map(([userId, group]) => (
          <Card
            key={userId}
            className="shadow-sm"
            title={
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Avatar size="small" icon={<UserSwitchOutlined />} className="mr-2" />
                  <Text strong>{getOwnerName(group.user) || "Unknown"}</Text>
                  <Text type="secondary" className="ml-2">({group.properties.length} properties)</Text>
                </div>
                {group.properties.length > 0 && (
                  <div className="flex space-x-2">
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSelectedUser(group.user.id);
                        setShowNewPropertyForm(true);
                      }}
                    >
                      Add Property
                    </Button>
                  </div>
                )}
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {group.properties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  cart={cart}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onRecurringOrders={canManageRecurrence ? setRecurringProperty : undefined}
                  onEditDateTime={() => handleEditDateTime(property)}
                  addToFromCart={addToFromCart}
                  removeFromCart={(propertyId) => removePropertyFromCart(propertyId)}
                  onToggleStatus={handleToggleStatus}
                  isSelected={selectedPropertyIds.includes(property.id)}
                  onSelect={(selected) => handlePropertySelection(property.id, selected)}
                  showOwner={false}
                  isAdmin={canManagePropertyStatus}
                />
              ))}
            </div>
          </Card>
        ))}
        {Object.keys(groupedProperties).length === 0 && (
          <div className="text-center text-gray-500 mt-6">
            No properties found matching your search.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center mb-6">
        <h2 className="text-2xl font-bold text-golden-navy flex items-center min-w-0">
          <img
            src="/ico/MyProperties.svg"
            alt="Properties Icon"
            className="inline-block mr-2 h-8"
          />
          {showNewPropertyForm
            ? "New Property Info"
            : editingPropertyId
              ? "Edit Property"
              : `Properties Management (${filteredProperties?.length || 0}/${total || 0})`}
        </h2>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Search Property"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-golden-blue focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchOutlined className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>

          <div className="w-full sm:w-64">
            <UserSelector
              value={selectedUser}
              roles={['customer', 'agency']}
              multiple={false}
              onChange={handleUserSelect}
              placeholder="Filter by client"
              style={{ width: '100%' }}
            />
          </div>

          <Dropdown overlay={viewMenu} trigger={['click']}>
            <Button>
              {groupByClient ? 'Grouped View' : viewMode === 'grid' ? 'Grid View' : 'List View'} <FilterOutlined />
            </Button>
          </Dropdown>

          <Dropdown overlay={sortMenu} trigger={['click']}>
            <Button icon={<FilterOutlined />}>
              Sort
            </Button>
          </Dropdown>

          {selectedPropertyIds.length > 0 && (
            <Dropdown
              overlay={bulkActionMenu}
              trigger={['click']}
              visible={bulkActionMenuVisible}
              onVisibleChange={setBulkActionMenuVisible}
            >
              <Button type="primary" className="bg-golden-navy">
                Bulk Actions ({selectedPropertyIds.length})
              </Button>
            </Dropdown>
          )}

          {selectedUser && <button
            className="flex items-center bg-golden-navy text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors duration-300"
            onClick={() => {
              if (editingPropertyId) {
                setEditingPropertyId(null);
              } else {
                setShowNewPropertyForm(!showNewPropertyForm);
              }
            }}
          >
            {!showNewPropertyForm && !editingPropertyId ? (
              <PlusOutlined className="h-5 w-5 mr-2" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {(showNewPropertyForm || editingPropertyId) ? "Cancel" : "Add New Property"}
          </button>}
        </div>
      </div>

      {(selectedUser || searchTerm || sortField !== "property_title" || sortOrder !== "asc") && (
        <div className="bg-blue-50 p-3 rounded-lg mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="mr-2 font-medium">Active Filters:</span>
            {selectedUser && (
              <Badge count={
                <div className="px-2 py-1 bg-blue-100 rounded-md text-blue-800 text-xs flex items-center mr-2">
                  <TeamOutlined className="mr-1" />
                  Client: {getSelectedUserDisplayName()}
                  <button
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    onClick={() => setSelectedUser(null)}
                  >
                    ×
                  </button>
                </div>
              } />
            )}
            {searchTerm && (
              <Badge count={
                <div className="px-2 py-1 bg-blue-100 rounded-md text-blue-800 text-xs flex items-center mr-2">
                  <SearchOutlined className="mr-1" />
                  "{searchTerm}"
                  <button
                    className="ml-1 text-blue-600 hover:text-blue-800"
                    onClick={() => setSearchTerm("")}
                  >
                    ×
                  </button>
                </div>
              } />
            )}
            {(sortField !== "property_title" || sortOrder !== "asc") && (
              <Badge count={
                <div className="px-2 py-1 bg-blue-100 rounded-md text-blue-800 text-xs flex items-center">
                  <FilterOutlined className="mr-1" />
                  Sort: {
                    sortField === "property_title" ? "Name" :
                      sortField === "created_at" ? "Date Added" :
                        "Last Modified"
                  } ({sortOrder === "asc" ? "A-Z" : "Z-A"})
                </div>
              } />
            )}
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {showNewPropertyForm ? (
        <NewPropertyForm onSave={handleSaveProperty} selectedUser={selectedUser} users={users} setShowNewPropertyForm={setShowNewPropertyForm} />
      ) : editingPropertyId ? (
        <EditPropertyForm
          propertyId={editingPropertyId}
          onSave={handleUpdateProperty}
          onCancel={handleCancelEdit}
          users={users}
        />
      ) : (
        <div className="w-full">
          {groupByClient ? (
            renderGroupedView()
          ) : viewMode === "grid" ? (
            <div
              style={{ maxHeight: "100vh" }}
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="rounded-lg overflow-y-auto px-3 pt-3 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3"
            >
              {filteredProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  cart={cart}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onRecurringOrders={canManageRecurrence ? setRecurringProperty : undefined}
                  onEditDateTime={() => handleEditDateTime(property)}
                  addToFromCart={addToFromCart}
                  removeFromCart={(propertyId) => removePropertyFromCart(propertyId)}
                  onToggleStatus={handleToggleStatus}
                  isSelected={selectedPropertyIds.includes(property.id)}
                  onSelect={(selected) => handlePropertySelection(property.id, selected)}
                  showOwner={true}
                  ownerName={getOwnerName(property.user_id)}
                  onImpersonateUser={() => handleImpersonateUser(property.user_id)}
                  isAdmin={canManagePropertyStatus}
                />
              ))}
              <div ref={bottomSentinelRef} className="col-span-full h-px" />
              {loadingMore && (
                <div className="col-span-full flex justify-center py-4">
                  <Spin />
                </div>
              )}
            </div>
          ) : (
            <div
              style={{ maxHeight: "100vh" }}
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="overflow-y-auto"
            >
              <PropertyTable
                properties={filteredProperties}
                onEdit={handleEdit}
                onRecurringOrders={canManageRecurrence ? setRecurringProperty : undefined}
                onDelete={handleDelete}
                onEditDateTime={handleEditDateTime}
                addToFromCart={addToFromCart}
                cart={cart}
                removeFromCart={(propertyId) => removePropertyFromCart(propertyId)}
                onToggleStatus={handleToggleStatus}
                canManageStatus={canManagePropertyStatus}
                statusUpdatingIds={statusUpdatingIds}
                selectedPropertyIds={selectedPropertyIds}
                onSelectProperty={handlePropertySelection}
                onSelectAll={handleSelectAllProperties}
                showOwner={true}
                getOwnerName={getOwnerName}
                onImpersonateUser={handleImpersonateUser}
                isAdmin={canManagePropertyStatus}
              />
              <div ref={bottomSentinelRef} className="h-px" />
              {hasMore && (
                <div className="flex justify-center py-4">
                  <Spin />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {filteredProperties?.length === 0 && !showNewPropertyForm && !editingPropertyId && !groupByClient && (
        <div className="text-center text-gray-500 mt-6">
          No properties found matching your search.
        </div>
      )}

      {recurringProperty && <Modal title={`Recurring orders — ${recurringProperty.property_title}`}
        open onCancel={() => setRecurringProperty(null)} footer={null} width={800} destroyOnClose>
        <PropertyRecurrencePanel propertyId={recurringProperty.id}
          arrivalTime={recurringProperty.arrival_time || '08:00'} completionTime={recurringProperty.completion_time || '09:30'} />
      </Modal>}

      {dateTimeModalVisible && selectedProperty && (
        <DateTimeModal
          isOpen={dateTimeModalVisible}
          onClose={handleDateTimeModalClose}
          propertyId={selectedProperty.id}
          initialData={{
            arrival_time: selectedProperty.arrival_time,
            completion_time: selectedProperty.completion_time,
            order_date: selectedProperty.property_date
          }}
          onSave={handleDateTimeSuccess}
        />
      )}
    </div>
  );
};

export default AdminPropertiesPage;
